import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, KeyRound, CheckCircle2 } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { authLogin, authRegister, authForgot } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const LoginPage = () => {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Create account dialog
  const [regOpen, setRegOpen] = useState(false);
  const [reg, setReg] = useState({ name: "", email: "", password: "", confirm: "" });
  const [regBusy, setRegBusy] = useState(false);

  // Forgot password dialog
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [recovered, setRecovered] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await authLogin(userId.trim(), password);
      login(user);
      navigate("/");
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Invalid loginname or password.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async () => {
    if (!reg.email || !reg.password) {
      toast({ title: "Email and password are required.", variant: "destructive" });
      return;
    }
    if (reg.password !== reg.confirm) {
      toast({ title: "Passwords do not match.", variant: "destructive" });
      return;
    }
    setRegBusy(true);
    try {
      await authRegister(reg.email.trim(), reg.password, reg.name.trim());
      toast({ title: "Account created", description: "You can now sign in." });
      setUserId(reg.email.trim());
      setPassword("");
      setRegOpen(false);
      setReg({ name: "", email: "", password: "", confirm: "" });
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Could not create account.", variant: "destructive" });
    } finally {
      setRegBusy(false);
    }
  };

  const handleForgot = async () => {
    if (!forgotEmail) {
      toast({ title: "Please enter your email.", variant: "destructive" });
      return;
    }
    setForgotBusy(true);
    setRecovered(null);
    try {
      const res = await authForgot(forgotEmail.trim());
      setRecovered(res.message || "Please contact your administrator to reset your password.");
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "No account found with this email.", variant: "destructive" });
    } finally {
      setForgotBusy(false);
    }
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setForgotEmail("");
    setRecovered(null);
  };

  return (
    <div className="min-h-screen w-full bg-[#f4f4f4] flex items-start justify-center pt-24">
      <div className="w-full max-w-[440px] px-6">
        <div className="mb-8">
          <Logo size="lg" />
          <div className="mt-3 flex justify-center">
            <span
              className="inline-block rounded-full border border-[#c6ddf0] bg-[#eaf3fb] px-5 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-[#3f7bbf]"
              data-testid="login-tax-portal-title"
            >
              Tax Portal
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-0">
          <div className="border-b border-gray-300 bg-white">
            <input
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="User ID"
              className="w-full px-4 py-3 text-sm outline-none placeholder:text-gray-400"
            />
          </div>

          <div className="border-b border-gray-300 bg-white flex items-center">
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-3 text-sm outline-none placeholder:text-gray-400"
            />
            <button type="button" onClick={() => setShowPw((s) => !s)} className="px-3 text-gray-500 hover:text-gray-700">
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div className="py-4" />

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#1877c9] hover:bg-[#0f5ea3] text-white font-medium py-3 rounded transition-colors disabled:opacity-60"
          >
            {submitting ? "Signing in..." : "Submit"}
          </button>
        </form>

        <div className="text-center mt-6 space-y-3">
          <button onClick={() => setRegOpen(true)} className="text-[#1877c9] hover:underline text-sm">
            No account? Create one!
          </button>
          <div>
            <button onClick={() => setForgotOpen(true)} className="text-[#1877c9] hover:underline text-sm">
              Forgot password
            </button>
          </div>
        </div>
      </div>

      {/* Create Account */}
      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Account</DialogTitle>
            <DialogDescription>Register a new Specfield Tax Services account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} placeholder="e.g. Ng Kok Chin" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} placeholder="name@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input type="password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} placeholder="Choose a password" />
            </div>
            <div className="space-y-1.5">
              <Label>Confirm password</Label>
              <Input type="password" value={reg.confirm} onChange={(e) => setReg({ ...reg, confirm: e.target.value })} placeholder="Re-enter password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegOpen(false)}>Cancel</Button>
            <Button onClick={handleRegister} disabled={regBusy} className="bg-[#1877c9] hover:bg-[#0f5ea3]">
              {regBusy ? "Creating..." : "Create Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forgot Password */}
      <Dialog open={forgotOpen} onOpenChange={(o) => (o ? setForgotOpen(true) : closeForgot())}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Retrieve Password</DialogTitle>
            <DialogDescription>Enter your registered email. For security, we no longer display passwords — your administrator can set a new one for you.</DialogDescription>
          </DialogHeader>
          {!recovered ? (
            <div className="space-y-3 py-1">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="name@example.com" />
              </div>
            </div>
          ) : (
            <div className="py-2">
              <div className="flex items-center gap-2 text-green-700 mb-3">
                <CheckCircle2 size={18} /> <span className="text-sm font-medium">Account found</span>
              </div>
              <div className="bg-[#f7f9fc] border border-gray-200 rounded p-4 flex items-start gap-2 text-sm text-gray-700">
                <KeyRound size={18} className="text-[#1877c9] mt-0.5 shrink-0" />
                <span>{recovered}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            {!recovered ? (
              <>
                <Button variant="outline" onClick={closeForgot}>Cancel</Button>
                <Button onClick={handleForgot} disabled={forgotBusy} className="bg-[#1877c9] hover:bg-[#0f5ea3]">
                  {forgotBusy ? "Checking..." : "Retrieve"}
                </Button>
              </>
            ) : (
              <Button onClick={closeForgot} className="bg-[#1877c9] hover:bg-[#0f5ea3]">Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
